
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import OrderDetails from "./OrderDetails";

interface Order {
  id: string;
  status: string;
  total_price: number;
  customer_name: string;
  customer_phone: string;
  delivery_address: string;
  payment_method: string;
  created_at: string;
}

interface OrderItem {
  id: string;
  product_id: string;
  quantity: number;
  price: number;
  notes: string;
  product: {
    name: string;
    description: string;
    image_url: string;
  };
}

interface OrdersTableProps {
  orders: Order[];
  selectedOrder: Order | null;
  orderItems: OrderItem[];
  onFetchOrderDetails: (order: Order) => void;
  onUpdateOrderStatus: (orderId: string, status: string) => void;
}

const OrdersTable = ({
  orders,
  selectedOrder,
  orderItems,
  onFetchOrderDetails,
  onUpdateOrderStatus,
}: OrdersTableProps) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-500";
      case "preparing":
        return "bg-blue-500";
      case "delivering":
        return "bg-purple-500";
      case "completed":
        return "bg-green-500";
      default:
        return "bg-gray-500";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Recebido";
      case "preparing":
        return "Preparando";
      case "delivering":
        return "Em Entrega";
      case "completed":
        return "Entregue";
      default:
        return status;
    }
  };

  return (
    <div className="bg-card text-card-foreground rounded-lg border border-border overflow-hidden shadow-sm">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">
          Pedidos ({orders.length})
        </h3>
      </div>

      {/* Versão Mobile: Cards sem necessidade de rolar para o lado */}
      <div className="md:hidden divide-y divide-border">
        {orders.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <p>Nenhum pedido encontrado</p>
            <p className="text-xs mt-1">Os pedidos aparecerão aqui quando os clientes fizerem compras</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order.id} className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm font-semibold text-foreground">
                  #{order.id.slice(0, 8).toUpperCase()}
                </span>
                <div className="flex items-center px-2 py-0.5 rounded-full bg-muted border border-border text-xs">
                  <div
                    className={`w-2 h-2 rounded-full mr-1.5 ${getStatusColor(order.status)}`}
                  />
                  <span className="font-medium text-foreground">{getStatusLabel(order.status)}</span>
                </div>
              </div>

              <div className="flex justify-between items-start text-sm">
                <div>
                  <p className="font-medium text-foreground">{order.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{order.customer_phone}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-krecao-yellow text-base">
                    R$ {order.total_price.toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(order.created_at)}
                  </p>
                </div>
              </div>

              <div className="pt-1">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onFetchOrderDetails(order)}
                      className="w-full border-border hover:bg-accent text-foreground font-medium"
                    >
                      Ver Detalhes
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="bg-background text-foreground border-border max-w-lg overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle className="text-foreground">
                        Detalhes do Pedido
                      </SheetTitle>
                    </SheetHeader>
                    {selectedOrder && (
                      <OrderDetails
                        order={selectedOrder}
                        orderItems={orderItems}
                        onUpdateStatus={onUpdateOrderStatus}
                      />
                    )}
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Versão Desktop: Tabela clássica */}
      <div className="hidden md:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-muted-foreground">Nº</TableHead>
              <TableHead className="text-muted-foreground">Cliente</TableHead>
              <TableHead className="text-muted-foreground">Data</TableHead>
              <TableHead className="text-muted-foreground">Total</TableHead>
              <TableHead className="text-muted-foreground">Status</TableHead>
              <TableHead className="text-muted-foreground">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="text-muted-foreground">
                    <p>Nenhum pedido encontrado</p>
                    <p className="text-sm mt-1">Os pedidos aparecerão aqui quando os clientes fizerem compras</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id} className="hover:bg-muted/50">
                  <TableCell className="font-mono text-sm">
                    #{order.id.slice(0, 8).toUpperCase()}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-foreground">{order.customer_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {order.customer_phone}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(order.created_at)}
                  </TableCell>
                  <TableCell className="font-bold text-krecao-yellow">
                    R$ {order.total_price.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <div
                        className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(
                          order.status
                        )}`}
                      ></div>
                      <span className="text-sm">{getStatusLabel(order.status)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Sheet>
                      <SheetTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onFetchOrderDetails(order)}
                          className="border-border hover:bg-accent text-foreground"
                        >
                          Ver Detalhes
                        </Button>
                      </SheetTrigger>
                      <SheetContent className="bg-background text-foreground border-border max-w-lg overflow-y-auto">
                        <SheetHeader>
                          <SheetTitle className="text-foreground">
                            Detalhes do Pedido
                          </SheetTitle>
                        </SheetHeader>
                        {selectedOrder && (
                          <OrderDetails
                            order={selectedOrder}
                            orderItems={orderItems}
                            onUpdateStatus={onUpdateOrderStatus}
                          />
                        )}
                      </SheetContent>
                    </Sheet>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default OrdersTable;
